<script setup>
import { WdsButtonLink, WdsPropertyCard } from '@wds/components';
import { DEFAULT_CURRENCY } from '@/config/currencies';
import { findPropertyTypeByKey } from '@/utils/propertyTypes';

const props = defineProps({
  properties: {
    type: Array,
    required: true,
  },
});

// ### COMPOSABLES
const { t } = useI18n();
const { useUrl } = useUrls();
const { useRating } = useRatings();
const { useImage } = useImages();
const { useFormatter } = useFormatters();
const $mq = useMq();

// ### DATA
const expandList = ref(false);

// ### COMPUTED
const maxNumVisibleCards = computed(() => {
  return $mq.isLargeScreen.value && !$mq.isExtraLargeScreen.value ? 6 : 8;
});

const hasValidProperties = computed(() => {
  return Array.isArray(props.properties) && props.properties.length > 0;
});

const propertyCardTranslations = computed(() => {
  return {
    tPrivatesFrom: t('t_PRIVATESFROM'),
    tDormsFrom: t('t_DORMSFROM'),
    tNoPrivatesAvailable: t('t_NOPRIVATESAVAILABLE'),
    tNoDormsAvailable: t('t_NODORMSAVAILABLE'),
    tNoAvailability: t('t_NOAVAILABILITY'),
    tPricesFrom: t('t_PRICESFROM'),
  };
});

const propertyListForCards = computed(() => {
  if (!hasValidProperties.value) {
    return [];
  }

  const visiblePropertyList = expandList.value
    ? props.properties
    : props.properties?.slice(0, maxNumVisibleCards.value);

  return visiblePropertyList.map((property) => {
    // Property page URL
    const propertyTypeInfo = findPropertyTypeByKey(property.type);
    const propertyPageUrl = useUrl.getPropertyPageUrl({
      propertyTypeSlug: t(propertyTypeInfo.slug),
      propertyId: property.id,
      urlFriendlyProperty: property.urlFriendlyName,
    });

    // Property minimum price
    const privateRoomsPrice = property.privateMinPrice?.value || 0;
    const dormRoomsPrice = property.sharedMinPrice?.value || 0;
    const pricesWithoutZeros = [privateRoomsPrice, dormRoomsPrice].filter((n) => Number(n) > 0).map(Number);
    const propertyMinPrice = pricesWithoutZeros.length ? Math.min.apply(0, pricesWithoutZeros) : null;
    const propertyMinPriceCurrency
      = property.sharedMinPrice?.currency || property.privateMinPrice?.currency || DEFAULT_CURRENCY.code;

    // Property image
    const propertyImage = useImage.getImage(property?.image?.medium) || null;

    return {
      id: property.id,
      bindProps: {
        name: property.name,
        type: t(propertyTypeInfo.transcode_s),
        typeTheme: propertyTypeInfo?.theme,
        href: propertyPageUrl,
        gallery: {
          slides: propertyImage ? [propertyImage] : [],
          alt: property.name,
          title: property.name,
        },
        rating: {
          score: property.avgRating,
          totalReviews: property.numberReviews,
          translations: toValue(useRating.getRatingScoreKeywordTranslations),
        },
        distance: t('t_FROMCITYCENTER', { DISTANCE: property.distance }),
        fromPrice: propertyMinPrice
          ? { curPrice: useFormatter.formatCurrency(propertyMinPrice, propertyMinPriceCurrency) }
          : null,
        translations: propertyCardTranslations.value,
      },
    };
  });
});

// ### METHODS
function toggleExpandedList() {
  expandList.value = !expandList.value;
}
</script>

<template>
  <div
    v-if="hasValidProperties"
    class="event-nearby-container"
  >
    <h2 class="event-nearby-title">
      {{ $t('t_OGEVENTNEARBYACCOMMODATIONS') }}
    </h2>
    <div class="event-nearby-properties">
      <WdsPropertyCard
        v-for="property in propertyListForCards"
        :key="`top-property-${property.id}`"
        class="event-nearby-property"
        orientation="vertical"
        v-bind="property.bindProps"
      />
    </div>
    <WdsButtonLink
      v-if="properties.length > maxNumVisibleCards"
      class="event-nearby-show-more"
      :text="$t(expandList ? 't_SHOWLESS' : 't_SHOWMORE')"
      :icon-end="expandList ? 'chevron-up' : 'chevron-down'"
      @click="toggleExpandedList"
    />
  </div>
</template>

<style lang="scss" scoped>
.event-nearby-container {
  display: flex;
  flex-direction: column;
  gap: $wds-spacing-m;

  .event-nearby-title {
    @include title-3-bld;

    overflow-wrap: break-word;
  }

  .event-nearby-properties {
    display: flex;
    flex-direction: column;
    gap: $wds-spacing-m;
  }

  .event-nearby-show-more {
    margin-top: $wds-spacing-m;
    margin-left: auto;
  }

  @include tablet {
    .event-nearby-properties {
      display: grid;
      grid-template-columns: repeat(2, 1fr);

      .event-nearby-property {
        min-width: auto;
      }
    }
  }

  @include tablet-large {
    .event-nearby-title {
      @include title-2-bld;
    }
  }

  @include desktop {
    .event-nearby-properties {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  @include desktop-large {
    .event-nearby-properties {
      grid-template-columns: repeat(4, 1fr);
    }
  }
}
</style>
